<p-toast></p-toast>

<p-panel header="Life Cycle Cost Analysis">
  <ng-template #icons>
    <div class="flex gap-2">
      <p-button icon="pi pi-plus" label="Add" severity="primary" (onClick)="addNew()" [disabled]="loading()" outlined>
      </p-button>
      <p-button icon="pi pi-pencil" label="Edit" severity="secondary" (onClick)="editLcca()"
        [disabled]="selectedLccas().length !== 1 || loading()" outlined>
      </p-button>
      <p-button icon="pi pi-trash" [label]="deleteButtonLabel()" severity="danger" (onClick)="deleteLcca()"
        [disabled]="selectedLccas().length === 0 || loading()" outlined>
      </p-button>
      <p-button icon="pi pi-refresh" label="Refresh" severity="secondary" (onClick)="refreshLccas()"
        [disabled]="loading()" outlined>
      </p-button>
      <p-button icon="pi pi-history" label="History" severity="secondary" (onClick)="showHistory()"
        [disabled]="loading() || selectedLccas().length !== 1" outlined>
      </p-button>
    </div>
  </ng-template>
  <ng-template #content>
    <!-- Search Panel -->
    <div class="mb-4 p-4 bg-surface-50 rounded-lg border border-surface-200">
      <div class="mb-3">
        <label for="lccaSearch" class="block text-sm font-medium text-surface-700 mb-1">Search by Maintenance Detail or
          LCCA Code</label>
        <div class="flex items-center gap-2">
          <div class="flex-1">
            <p-iconfield iconPosition="left">
              <p-inputicon>
                <i class="pi pi-search"></i>
              </p-inputicon>
              <input id="lccaSearch" pInputText type="text" [(ngModel)]="keywords" (ngModelChange)="onFilter($event)"
                placeholder="Enter search terms..." autocomplete="off" />
            </p-iconfield>
          </div>
          <p-button *ngIf="keywords.trim()" icon="pi pi-times" label="Clear Search" severity="secondary" size="small"
            (onClick)="clearFilter()" outlined>
          </p-button>
        </div>
      </div>
    </div>

    <p-divider></p-divider>
    <p-table [value]="(lccas() || [])" [totalRecords]="(totalRecords() || 0)" [paginator]="true" [rows]="rows"
      [first]="first" [lazy]="true" [loading]="loading()" [loadingIcon]="'pi pi-spin pi-cog'"
      [tableStyle]="{ 'min-width': '30rem' }" [showCurrentPageReport]="true"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} LCCAs" paginatorDropdownAppendTo="body" [rowsPerPageOptions]="[10, 25, 50]"
      (onPage)="onPage($event)" selectionMode="multiple" [(selection)]="selectedLccas"
      dataKey="id" size="small">
      <ng-template pTemplate="header">
        <tr>
          <th style="width: 3rem">
            <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
          </th>
          <th>Asset Type</th>
          <th>No.</th>
          <th>Maintenance Detail</th>
          <th>Maintenance Type</th>
          <th>Frequency Value</th>
          <th>Frequency Period</th>
          <th>Rate</th>
          <th>Unit</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-lcca>
        <tr [pSelectableRow]="lcca">
          <td>
            <p-tableCheckbox [value]="lcca"></p-tableCheckbox>
          </td>
          <td>{{ getAssetTypeName(lcca.asset_type) }}</td>
          <td>{{ lcca.lcca_code }}</td>
          <td>{{ lcca.maintenance_detail }}</td>
          <td>{{ getMaintenanceTypeName(lcca.maintenanc_type) }}</td>
          <td>{{ lcca.frequency_value }}</td>
          <td>{{ getPeriodicalName(lcca.frequency_period) }}</td>
          <td>{{ lcca.rate }}</td>
          <td>{{ getUnitName(lcca.unit) }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="9" class="text-center p-4">No Life Cycle Cost Analysis found</td>
        </tr>
      </ng-template>
    </p-table>
  </ng-template>
</p-panel>

<p-confirmDialog></p-confirmDialog>

<p-dialog [visible]="dialogVisible()" (visibleChange)="dialogVisible.set($event)" [modal]="true" [draggable]="false"
  [resizable]="false" [closable]="false" [dismissableMask]="true" [closeOnEscape]="true" [style]="{width: '600px'}"
  [header]="isEditMode() ? 'Edit LCCA' : 'Add LCCA'">

  <div class="p-fluid">
    <div class="mb-4 flex flex-col gap-2">
      <label for="assetType" class="font-medium">Asset Type</label>
      <app-type-autocomplete-2 [(value)]="assetType" [placeholder]="'Search asset type...'" />
    </div>

    <div class="mb-4 grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label for="lccaCode" class="font-medium">No.</label>
        <input id="lccaCode" type="text" pInputText [ngModel]="lccaCode()" (ngModelChange)="lccaCode.set($event)"
          placeholder="Enter LCCA code" class="w-full">
      </div>

      <div class="flex flex-col gap-2">
        <label for="trigger" class="font-medium">Trigger</label>
        <input id="trigger" type="text" pInputText [ngModel]="trigger()" (ngModelChange)="trigger.set($event)"
          placeholder="Enter Trigger" class="w-full">
      </div>
    </div>

    <div class="mb-4 flex flex-col gap-2">
      <label for="maintenanceDetail" class="font-medium">Maintenance Detail</label>
      <input id="maintenanceDetail" type="text" pInputText [ngModel]="maintenanceDetail()"
        (ngModelChange)="maintenanceDetail.set($event)" placeholder="Enter Maintenance Detail" class="w-full">
    </div>

    <div class="mb-4 flex flex-col gap-2">
      <label for="maintenanceType" class="font-medium">Maintenance Type</label>
      <app-lcca-type-autocomplete [(value)]="maintenanceType" />
    </div>

    <div class="mb-4 grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label for="frequencyValue" class="font-medium">Frequency Value</label>
        <input id="frequencyValue" type="text" pInputText [ngModel]="frequencyValue()"
          (ngModelChange)="frequencyValue.set($event)" placeholder="Enter Frequency Value" class="w-full">
      </div>

      <div class="flex flex-col gap-2">
        <label for="frequencyPeriod" class="font-medium">Frequency Period</label>
        <app-lcca-periodical-autocomplete [(value)]="frequencyPeriod" [placeholder]="'Search frequency period...'" />
      </div>
    </div>

    <div class="mb-4 grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label for="rate" class="font-medium">Rate</label>
        <input id="rate" type="text" pInputText [ngModel]="rate()" (ngModelChange)="rate.set($event)"
          placeholder="Enter Rate" class="w-full">
      </div>

      <div class="flex flex-col gap-2">
        <label for="unit" class="font-medium">Unit</label>
        <app-lcca-unit-autocomplete [(value)]="unit" [placeholder]="'Search unit...'" />
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex gap-2 justify-end">
      <p-button label="Cancel" icon="pi pi-times" (onClick)="closeDialog()" [disabled]="savingLcca()"
        severity="secondary" outlined>
      </p-button>
      <p-button [label]="isEditMode() ? 'Update' : 'Add'" icon="pi pi-check" (onClick)="saveLcca()"
        [disabled]="savingLcca()" [loading]="savingLcca()">
      </p-button>
    </div>
  </ng-template>
</p-dialog>

<app-asset-history-dialog
  [(visible)]="historyVisible"
  [resourceName]="auditResourceName"
  [recordId]="historyRecordId"
  [recordLabel]="historyRecordLabel">
</app-asset-history-dialog>
